'use client';
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Paper, Stack, Box, Typography, Grid } from '@mui/material';
import { base64GrayImage } from '@peerless/ui-assets/base64ImageUrl';
import { Link } from './Link';
import { ProductTitle } from './ProductTitle';

export interface QuickSearchProduct {
  title: string;
  sku: string;
  thumbnail: string | null;
}

/**
 * Product result from the quick search.
 */
export interface QuickSearchResult<TProduct extends QuickSearchProduct = QuickSearchProduct> {
  count: number;
  keyword: string;
  products: Array<TProduct>;
}

/**
 * Hook signature to search for products.
 */
export type QuickSearchHook<TProduct extends QuickSearchProduct = QuickSearchProduct> = () => {
  data: QuickSearchResult<TProduct>;
  isLoading: boolean;
  search(keyword: string): void;
  reset(): void;
};

export interface QuickSearchResultBlockProps<
  TProduct extends QuickSearchProduct = QuickSearchProduct,
> {
  title: string;
  queryKey: string;
  keyword: string | null;
  useSearch: QuickSearchHook<TProduct>;

  getProductUrl?(result: TProduct): string;
  getAllResultsUrl?(keyword: string): string;
  FooterComponent?(result: QuickSearchResult): React.ReactNode;

  getProductTitle?(result: TProduct): string;
  ProductDescriptionComponent?(result: TProduct): React.ReactNode;
  hasProductImages?: false;

  onNavigate(url: string): void;

  onStatusChange(source: string, isLoading: boolean): void;
  onInteracting(source: string, active: boolean): void;
  onResultChange(source: string, status: boolean): void;
}

export function defaultProductUrl({ sku }: QuickSearchProduct): string {
  return `/products/${sku}`;
}

export function defaultProductTitle({ title }: QuickSearchProduct): string {
  return title;
}

export function DefaultProductDescriptionComponent({ sku }: QuickSearchProduct): React.ReactNode {
  return (
    <Typography component="span" variant="body2" sx={{ color: 'gray.dark' }}>
      {sku}
    </Typography>
  );
}

export interface QuickSearchResultBlockHandle {
  key: string;
  reset: () => void;
}

const MIN_KEYWORD_LENGTH = 3;

export const QuickSearchResultBlock = forwardRef<
  QuickSearchResultBlockHandle,
  QuickSearchResultBlockProps
>(
  (
    {
      title,
      queryKey,
      keyword,
      useSearch,
      getProductUrl = defaultProductUrl,
      getProductTitle = defaultProductTitle,
      ProductDescriptionComponent = DefaultProductDescriptionComponent,
      hasProductImages,
      getAllResultsUrl,
      FooterComponent,
      onNavigate,
      onStatusChange,
      onResultChange,
      onInteracting,
    },
    ref
  ) => {
    const { data, isLoading, search, reset } = useSearch();

    const hasResult = data.count > 0;
    const interacting = !data.keyword || data.keyword !== keyword;

    const t = useTranslations('Search');

    useImperativeHandle(ref, () => ({ key: queryKey, reset }), [queryKey, reset]);

    useEffect(() => {
      if (keyword && keyword.length >= MIN_KEYWORD_LENGTH) {
        search(keyword);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- search is also using query, will result in self referencing issue if included in the depenency list
    }, [keyword]);

    useEffect(() => {
      onStatusChange(queryKey, isLoading);
    }, [onStatusChange, queryKey, isLoading]);

    useEffect(() => {
      onInteracting(queryKey, interacting);
    }, [onInteracting, queryKey, interacting]);

    useEffect(() => {
      onResultChange(queryKey, hasResult);
    }, [onResultChange, queryKey, hasResult]);

    if (isLoading || interacting || !hasResult) {
      return null;
    }

    const allResultsUrl = getAllResultsUrl?.(keyword);

    return (
      <Box padding={2}>
        <Typography variant={'h4'} className="header-underline">
          {title}
        </Typography>
        <Stack gap={2}>
          <Grid container spacing={1}>
            {data.products.map(result => (
              <Grid item xs={12} key={result.sku}>
                <Paper
                  sx={{ cursor: 'pointer', '&:hover h5': { color: 'primary.main' } }}
                  elevation={0}
                  onClick={() => onNavigate(getProductUrl(result))}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    {hasProductImages !== false && (
                      <Box data-product-sku={result.sku}>
                        <Image
                          src={result.thumbnail ?? base64GrayImage}
                          alt={result.title}
                          height={60}
                          width={60}
                        />
                      </Box>
                    )}
                    <Stack>
                      <ProductTitle variant="h5" title={getProductTitle(result)} maxLine={1} />
                      <ProductDescriptionComponent {...result} />
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
          {hasResult && !!FooterComponent && <FooterComponent {...data} />}
          {hasResult && allResultsUrl && data.count > data.products.length && (
            <Link
              href={allResultsUrl}
              underline="hover"
              onClick={evt => {
                evt.preventDefault();
                onNavigate(allResultsUrl);
              }}
            >
              {t('view-all-results', { count: data.count })}
            </Link>
          )}
        </Stack>
      </Box>
    );
  }
);

QuickSearchResultBlock.displayName = 'QuickSearchResultBlock';
